import JourneyOfferList from '@/components/purchase/journey/JourneyOfferList';
import {
  setSelectedOutboundOfferMapByLegId,
  updateSelectedOutboundOfferMapByLegId,
} from '@/features/purchase/purchaseActions';
import { selectedOutboundOfferMapSelector } from '@/features/purchase/purchaseSelectors';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { SearchJourneyOffersDTO } from '@/types/dto/agent';
import { GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import { journeyChangePath } from '@/utils/journeyChangeUrlParams';
import { getDefaultOfferMap } from '@/utils/purchase';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Fare selection step of the journey-change deep-link flow, reusing the purchase
// `JourneyOfferList`, which reads the chosen offers from the outbound slice. This
// step only seeds the default (cheapest) offer map; `JourneyChangeCheckout`
// commits whatever ends up selected.
const JourneyChangeOfferSelect = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedJourney = (
    location.state as { journey?: SearchJourneyOffersDTO } | null
  )?.journey;

  // Seed while the map is empty, not once on mount: returning from checkout
  // remounts this step before `Checkout`'s un-awaited `abandonExchange()`
  // cleanup has run its `resetPurchase()`, which then empties the map. An empty
  // map only ever means "nothing selected", so this can't overwrite a choice.
  const selectedOfferMap = useSelector(selectedOutboundOfferMapSelector);
  useEffect(() => {
    if (!selectedJourney || Object.keys(selectedOfferMap).length > 0) return;
    dispatch(
      setSelectedOutboundOfferMapByLegId(getDefaultOfferMap(selectedJourney))
    );
  }, [dispatch, selectedJourney, selectedOfferMap]);

  // No trip in hand (a direct visit or refresh drops router state) — back to
  // the selection step, keeping the deep-link params.
  useEffect(() => {
    if (!selectedJourney) {
      navigate(journeyChangePath(location.search), { replace: true });
    }
  }, [selectedJourney, location.search, navigate]);

  if (!selectedJourney) return null;

  return (
    <JourneyOfferList
      title={<TransText i18nKey="selectTicket" />}
      selectedJourney={selectedJourney}
      isOutbound
      updateSelectedOfferMap={updateSelectedOutboundOfferMapByLegId}
      selectedOfferMapByLegIdSelector={selectedOutboundOfferMapSelector}
      onContinue={() => {
        trackCustomEvent(GTM_EVENT.OUTBOUND_OFFER_CONFIRMED);
        navigate(journeyChangePath(location.search, 'checkout'), {
          state: { journey: selectedJourney },
        });
      }}
      onGoBack={() => navigate(journeyChangePath(location.search))}
    />
  );
};

export default JourneyChangeOfferSelect;
